// axiosBaseQuery.ts
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios, { AxiosError } from "axios";
import type { AxiosRequestConfig } from "axios";

// Get API URL from environment variable with fallback
const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/';

if (!import.meta.env.VITE_API_URL) {
  console.warn('VITE_API_URL is not set. Using default: http://localhost:8000/api/');
  console.warn('Please create a .env file in the root directory with: VITE_API_URL=http://localhost:3000/api/');
}

export const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
    },
    unknown,
    unknown
  > =>
    async ({ url, method = "GET", data, params, headers }) => {
      const accessToken = localStorage.getItem("accessToken");
      
      // Determine Content-Type based on data type
      const isFormData = data instanceof FormData;
      const contentType = isFormData ? undefined : "application/json";

      try {
        const result = await axios({
          url: apiUrl + url,
          method,
          data: data,
          params,
          headers: {
            ...(contentType && { "Content-Type": contentType }),
            ...headers,
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
          },
        });

        return { data: result.data };
      } catch (error) {
        const err = error as AxiosError;

        // If token expired
        if (err.response?.status === 401 && !url.includes("auth/refreshToken") && !url.includes("auth/login")) {
          try {
            const refreshToken = localStorage.getItem("refreshToken");
            const refreshResponse = await axios.post(
              `${apiUrl}auth/refreshToken`,
              {
                refreshToken,
              }
            );

            const newAccessToken = refreshResponse.data.data.accessToken;
            localStorage.setItem("accessToken", newAccessToken);

            // Retry original request with new token
            const retryIsFormData = data instanceof FormData;
            const retryContentType = retryIsFormData ? undefined : "application/json";
            const retryResult = await axios({
              url: apiUrl + url,
              method,
              data,
              params,
              headers: {
                ...(retryContentType && { "Content-Type": retryContentType }),
                ...headers,
                Authorization: `Bearer ${newAccessToken}`,
              },
            });

            return { data: retryResult.data };
          } catch {
            // Refresh failed — log out user or handle appropriately
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            return {
              error: {
                status: 401,
                data: "Unauthorized - Refresh Token Failed",
              },
            };
          }
        }

        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    };
